export class PopperBinding {
  /** Boolean: set to true for right-aligned (defaults to left-aligned). */
  public alignRight = "<?";

  /** fn called when the popper is opened */
  public onOpened = "&?";

  /** fn called when the popper is closed */
  public onClosed = "&?";

  /** fn called when the popper is initialized
   *  Parameter passed along is of type ServiceDesk.Controls.Popper.Popper which exposes methods and properties that can be used to interact with the popper.
   */
  public onInitialized = "&?";

  /** Boolean. Optional. If set to true, the popper when opened will default focus to an outer container around popper content.
   *  By default its false, and it is expected from the consumer of the control to implement focus when popper is opened.
   */
  public showFocus = "<?";

  /**
   * The next click anywhere on the page will close the dialog. (including clicks inside)
   */
  public closeOnNextClick = "<?";

  /**
   * True if popper has a hover state.
   */
  public isHover = "<?";

  /**
   * Trigger popper menu on right click instead of left click.
   */
  public isRightClickTrigger = "<?";
}

/** Exposes options on popper that can be used to interact with it. */
export interface PopperDelegate {
  /** fn to be used to open the popper */
  open: () => void;

  /** fn to be used to close the popper */
  close: () => void;

  /** gives the id of the popper that contains popped out element */
  id: string;
}

export interface WindowTooltipster extends JQuery {
  tooltipster: any;
}

type Position = { coord: { left: number; top: number }; size: { width: number } };

export class PopperController {
  private triggerElement: WindowTooltipster;
  private contentElement: WindowTooltipster;
  private elementInstance: WindowTooltipster;
  private instance: any;
  private alignRight: boolean;
  private isHover: boolean;
  private onOpened: () => void;
  private onClosed: () => void;
  private onInitialized: (arg: { popper: PopperDelegate }) => void;
  private clickListener: () => void;
  public isVisible = false;
  public popperId: string;
  public showFocus: boolean;
  public closeOnNextClick: boolean;
  public isRightClickTrigger = false;
  // @ngInject
  constructor(private $scope: ng.IScope, public $element: ng.IAugmentedJQuery, private $window: ng.IWindowService) {}

  public $onDestroy() {
    if (this.clickListener) {
      this.$window.removeEventListener("click", this.clickListener);
    }
  }

  public $postLink() {
    if (this.closeOnNextClick) {
      this.clickListener = (() => {
        this.closePopper();
      }).bind(this);
      this.$window.addEventListener("click", this.clickListener, true);
    }

    this.$scope.$evalAsync(() => {
      this.contentElement = <WindowTooltipster>this.$element.find(".sd-popper-content");
      this.triggerElement = <WindowTooltipster>this.$element.find(".sd-popper-trigger");
      this.initialize();
    });
  }

  public initialize() {
    this.intializeTooltipster();
    let popperDelegateObj: PopperDelegate = {
      open: this.openPopper.bind(this),
      close: this.closePopper.bind(this),
      id: this.popperId
    };

    if (angular.isFunction(this.onInitialized)) {
      this.onInitialized({ popper: popperDelegateObj });
    }
    // Call open/close the tooltipster to move popper content to ultimate location
    // It'll correct the watch count within the control
    this.elementInstance.tooltipster("open");
    this.elementInstance.tooltipster("close");
  }

  public intializeTooltipster() {
    this.elementInstance = this.triggerElement.tooltipster({
      trigger: "custom",
      content: this.contentElement,
      parent: ".sd-popper-container",
      position: "bottom",
      arrow: true,
      interactive: true,
      animationDuration: 0,
      triggerClose: {
        click: true
      },
      functionPosition: (notUsed1: {}, notUsed2: {}, position: any) => {
        return this.positionPopper(position);
      },
      functionAfter: (notUsed1: {}, notUsed2: {}) => {
        this.isVisible = false;
      }
    });

    // get the tooltipster instance
    this.instance = (<any>$).tooltipster.instances(this.triggerElement)[0];

    this.popperId = this.instance.__namespace;

    // add this popperId to the popper-trigger element so that we have a way of linking those together
    this.triggerElement[0].classList.add(this.popperId);
  }

  public openPopper() {
    try {
      this.elementInstance.tooltipster("open");
    } catch (e) {
      this.intializeTooltipster();
      this.elementInstance.tooltipster("open");
    }
    this.isVisible = true;
    if (this.showFocus) {
      this.contentElement.focus();
    }

    if (angular.isFunction(this.onOpened)) {
      this.onOpened();
    }
  }

  public closePopper(nextClickListener = false) {
    try {
      if (nextClickListener) {
        let instance = <WindowTooltipster>this.elementInstance.filter(function () {
          return $(this).data("tooltipster-ns");
        });
        instance.tooltipster("close");
      } else {
        this.elementInstance.tooltipster("close");
      }

      this.isVisible = false;
      if (angular.isFunction(this.onClosed)) {
        this.onClosed();
        this.elementInstance.focus();
      }
    } catch (error) {
      console.warn(`closePopper error ${error}`);
    }
  }

  public togglePopper() {
    if (!this.isVisible) {
      this.openPopper();
    } else {
      this.closePopper();
    }
  }

  private positionPopper(position: Position): Position {
    let triggerElementCoordinates = this.triggerElement[0].getBoundingClientRect();
    if (this.isHover) {
      position.coord.left = this.alignRight
        ? triggerElementCoordinates.left - position.size.width
        : triggerElementCoordinates.right;
      position.coord.top = triggerElementCoordinates.top;
    } else if (this.alignRight) {
      position.coord.left = triggerElementCoordinates.left + this.triggerElement.outerWidth() - position.size.width;
    } else {
      position.coord.left = triggerElementCoordinates.left;
    }

    return position;
  }
}
export const sdPopper: ng.IComponentOptions = {
  controller: PopperController,
  transclude: true,
  template: "<div class='sd-popper'><ng-transclude></ng-transclude></div>",
  bindings: <any>new PopperBinding()
};
